import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, currentMonth, monthLabel, formatApiError } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { useTaxonomy } from "../../lib/useTaxonomy";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const CoachForm = ({ initial, onSave, onClose, saveLabel }) => {
  const [form, setForm] = useState(initial);
  const { batchNames } = useTaxonomy();
  const toggleBatch = (b) => {
    const set = new Set(form.batches);
    set.has(b) ? set.delete(b) : set.add(b);
    setForm({ ...form, batches: [...set] });
  };
  // Always include batches the coach is already assigned to (even if renamed/removed) so admin can uncheck them
  const options = [...new Set([...batchNames, ...(form.batches || [])])];
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 mb-6" data-testid="coach-form">
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <input required placeholder="Coach name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="coach-name" />
        <input placeholder="Phone (optional)" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="coach-phone" />
      </div>
      <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Assigned Batches</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {options.map((b) => (
          <button type="button" key={b} onClick={() => toggleBatch(b)}
            data-testid={`coach-batch-${b.replace(/[^a-z0-9]+/gi, "-")}`}
            className={`text-xs px-3 py-2 border transition-colors duration-150 ${form.batches.includes(b) ? "bg-[#D4AF37] text-black border-[#D4AF37] font-bold" : "border-white/20 text-white/60 hover:border-[#D4AF37]"}`}>
            {b}
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <button type="submit" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="coach-save">{saveLabel}</button>
        <button type="button" onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]" data-testid="coach-cancel">Cancel</button>
      </div>
    </form>
  );
};

export default function Coaches() {
  const [coaches, setCoaches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [sessions, setSessions] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  const saveCoachLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/coaches/${loginFor.id}/login`, loginForm);
      toast.success(`Coach login ready: ${loginForm.email} / ${loginForm.password} — share with ${loginFor.name}. They'll see only Attendance and Reports for their batches.`);
      setLoginFor(null);
      setLoginForm({ email: "", password: "" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const revokeLogin = async (coach) => {
    try {
      await api.delete(`/admin/coaches/${coach.id}/login`);
      toast.success(`${coach.name}'s login revoked — they can no longer sign in.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = () => api.get("/admin/coaches").then((r) => setCoaches(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/admin/coaches/${selected.id}/sessions`, { params: { month } })
      .then((r) => setSessions(r.data)).catch(() => {});
  }, [selected, month]);

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/admin/coaches/${editing.id}`, form);
        toast.success("Coach updated");
        if (selected?.id === editing.id) setSelected({ ...selected, ...form });
      } else {
        await api.post("/admin/coaches", form);
        toast.success("Coach added");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (coach) => {
    try {
      await api.delete(`/admin/coaches/${coach.id}`);
      toast.success(`${coach.name} removed`);
      if (selected?.id === coach.id) { setSelected(null); setSessions(null); }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-coaches">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Coaches <span className="text-[#D4AF37]">({coaches.length})</span></h1>
        <button onClick={() => { setEditing(null); setShowForm(!showForm); }} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="coach-add">
          <Plus size={15} /> Add Coach
        </button>
      </div>

      {showForm && <CoachForm initial={editing || { name: "", phone: "", batches: [] }} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} saveLabel={editing ? "Save Changes" : "Add Coach"} />}

      <div className="grid sm:grid-cols-2 gap-3 mb-10">
        {coaches.map((c) => (
          <div key={c.id}
            onClick={() => setSelected(c)}
            className={`border p-5 cursor-pointer transition-colors duration-200 ${selected?.id === c.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 bg-[#0A0A0A] hover:border-[#D4AF37]/40"}`}
            data-testid={`coach-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-bold">{c.name}</p>
                {c.phone && <p className="text-white/40 text-xs mt-0.5">{c.phone}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); setLoginFor(loginFor?.id === c.id ? null : c); }} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-150" title="Create / reset coach login" data-testid={`coach-login-btn-${c.id}`}><KeyRound size={16} /></button>
                <button onClick={(e) => { e.stopPropagation(); setEditing(c); setShowForm(true); }} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-150" data-testid={`coach-edit-${c.id}`}><Pencil size={16} /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`coach-remove-${c.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
            {loginFor?.id === c.id && (
              <div onClick={(e) => e.stopPropagation()} className="mt-4 space-y-3">
                <form onSubmit={saveCoachLogin} className="flex flex-wrap gap-2" data-testid={`coach-login-form-${c.id}`}>
                  <input required type="email" placeholder="Coach email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls + " flex-1 min-w-[160px]"} data-testid={`coach-login-email-${c.id}`} />
                  <input required type="text" minLength={8} placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls + " flex-1 min-w-[160px]"} data-testid={`coach-login-pass-${c.id}`} />
                  <button type="submit" className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#F3D059]" data-testid={`coach-login-save-${c.id}`}>{c.has_login ? "Reset" : "Save"}</button>
                </form>
                {c.has_login ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[10px] uppercase tracking-widest text-emerald-400" data-testid={`coach-login-active-${c.id}`}>Login active</span>
                    <button onClick={() => revokeLogin(c)} className="text-[10px] uppercase tracking-widest text-red-400 border border-red-400/40 px-3 py-1.5 rounded-sm hover:bg-red-400/10 transition-colors duration-150" data-testid={`coach-revoke-${c.id}`}>Revoke Access</button>
                  </div>
                ) : (
                  <p className="text-white/30 text-[10px] uppercase tracking-widest" data-testid={`coach-login-none-${c.id}`}>No login yet</p>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              {c.batches.length === 0 && <span className="text-white/30 text-xs">No batches assigned</span>}
              {c.batches.map((b) => (
                <span key={b} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37]">{b}</span>
              ))}
            </div>
          </div>
        ))}
        {coaches.length === 0 && !showForm && (
          <p className="text-white/40 text-sm border border-white/10 p-8 text-center sm:col-span-2">No coaches yet. Add your first coach and assign batches.</p>
        )}
      </div>

      {selected && (
        <div data-testid="coach-sessions">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">
            Sessions Completed — {selected.name}
          </p>
          <div className="mb-6">
            <MonthNav month={month} onChange={setMonth} testid="coach-month-nav" />
          </div>
          {sessions && (
            <>
              <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 mb-6 inline-block" data-testid="coach-total-sessions">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Total Sessions in {monthLabel(month)}</p>
                <p className="font-heading text-4xl font-black text-[#D4AF37] mt-1">{sessions.total_sessions}</p>
              </div>
              <div className="border border-white/10">
                {sessions.sessions.length === 0 && <p className="text-white/40 text-sm p-6 bg-[#0A0A0A]">No sessions marked for {selected.name}'s batches in {monthLabel(month)}.</p>}
                {sessions.sessions.map((s) => (
                  <div key={`${s.date}-${s.batch}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 last:border-0 p-4 bg-[#0A0A0A]" data-testid={`coach-session-${s.date}`}>
                    <div>
                      <p className="font-semibold text-sm">{s.date}</p>
                      <p className="text-white/40 text-xs mt-0.5">{s.batch}</p>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="text-[#D4AF37] font-semibold">{s.present} present</span>
                      <span className="text-red-400">{s.absent} absent</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
